import { useState } from 'react';

import { AppIcon, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import { WriteDialog } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/WriteDialog';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { useGetInstitutionCoachesQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  isSubmittableCode,
  isSubmittableCohortName,
  randomCode,
  type AdminRoster,
  type CohortDraft,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';

import { CodeField } from './CodeField';

const { createCohortDialog: strings, coachesDialog: coachStrings } =
  appStrings.modules.rostersAdmin;
const { create: createStrings } = appStrings.modules.coachingDashboard.institutionAdmin.cohorts;

const FORM_ID = 'rosters-admin-create-cohort-form';
const SEARCH_DEBOUNCE_MS = 300;

export type CreateCohortSubmission = Readonly<{
  rosterId: number;
  draft: CohortDraft;
}>;

type CreateCohortDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  rosters: readonly AdminRoster[];
  rosterName: (roster: AdminRoster) => string;
  /** A roster the dialog opens already pointed at, from a row action; null asks for one. */
  initialRosterId: number | null;
  isSubmitting: boolean;
  isError: boolean;
  errorMessage?: string | null;
  isWriteInFlight: boolean;
  isRostersUnavailable: boolean;
  /** Resolves true once the cohort landed; the dialog then closes itself. */
  onSubmit: (submission: CreateCohortSubmission) => Promise<boolean>;
}>;

export const CreateCohortDialog = ({
  open,
  onClose,
  rosters,
  rosterName,
  initialRosterId,
  isSubmitting,
  isError,
  errorMessage,
  isWriteInFlight,
  isRostersUnavailable,
  onSubmit,
}: CreateCohortDialogProps) => {
  const [rosterId, setRosterId] = useState<number | null>(initialRosterId);
  const [name, setName] = useState('');
  const [coaches, setCoaches] = useState<CoachSummary[]>([]);
  const [code, setCode] = useState('');
  const [coachInput, setCoachInput] = useState('');
  const debouncedCoachInput = useDebounce(coachInput, SEARCH_DEBOUNCE_MS);

  // A coach is attachable only where they hold a seat at the roster's own institution,
  // so offering the context's whole reach would offer picks the create cannot accept.
  const coachInstitutionId =
    rosters.find((roster) => roster.id === rosterId)?.managingInstitution.id ?? null;
  const isRosterChosen = rosterId !== null;

  const {
    data: institutionCoaches,
    isFetching: isFetchingCoaches,
    isError: isCoachesError,
    error: coachesError,
  } = useGetInstitutionCoachesQuery(
    {
      q: debouncedCoachInput,
      institutionIds: coachInstitutionId === null ? undefined : [coachInstitutionId],
    },
    { skip: !open || !isRosterChosen },
  );

  const onRosterChange = (next: number | null) => {
    setRosterId(next);
    setCoaches([]);
  };

  const canSubmit =
    rosterId !== null &&
    !isRostersUnavailable &&
    !isSubmitting &&
    !isWriteInFlight &&
    isSubmittableCohortName(name) &&
    (code.length === 0 || isSubmittableCode(code));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmit || rosterId === null) return;

    const landed = await onSubmit({
      rosterId,
      draft: {
        name,
        startDate: null,
        endDate: null,
        code: code.length === 0 ? null : code,
        coachUserIds: coaches.map((coach) => coach.id),
      },
    });

    if (landed) onClose();
  };

  return (
    <WriteDialog
      id="rosters-admin-create-cohort-dialog"
      open={open}
      onClose={onClose}
      title={strings.title}
      footer={
        <>
          <Button
            id="rosters-admin-create-cohort-dialog-close"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClose}
          >
            {strings.cancel}
          </Button>
          <Button
            id="rosters-admin-create-cohort-dialog-submit"
            hierarchy="Primary"
            size="md"
            form={FORM_ID}
            type="submit"
            disabled={!canSubmit}
          >
            {isSubmitting ? strings.submitting : strings.submit}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id={FORM_ID}
        onSubmit={handleSubmit}
        sx={formSx}
        aria-label={strings.title}
      >
        <LabeledFilterSelect
          id="rosters-admin-create-cohort-roster"
          label={strings.rosterLabel}
          placeholder={strings.rosterPlaceholder}
          value={rosterId === null ? '' : String(rosterId)}
          options={rosters.map((roster) => ({
            value: String(roster.id),
            label: rosterName(roster),
          }))}
          onChange={(next) => onRosterChange(next === '' ? null : Number(next))}
          sx={rosterSelectSx}
        />
        <Box sx={sectionSx}>
          <Typography
            component="label"
            variant="text-sm-medium"
            htmlFor="rosters-admin-create-cohort-name"
            sx={fieldLabelSx}
          >
            {strings.nameLabel}
          </Typography>
          <TextField
            id="rosters-admin-create-cohort-name"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Box>
        <Box sx={codeRowSx}>
          <CodeField
            idPrefix="rosters-admin-create-cohort-code"
            code={code}
            onCodeChange={setCode}
            isError={isError}
          />
          <Button
            id="rosters-admin-create-cohort-code-generate"
            hierarchy="Secondary gray"
            size="md"
            type="button"
            onClick={() => setCode(randomCode())}
          >
            {appStrings.modules.rostersAdmin.createCodeDialog.generate}
          </Button>
        </Box>
        <Box sx={sectionSx}>
          <Typography
            component="label"
            variant="text-sm-medium"
            htmlFor="rosters-admin-create-cohort-coach-picker"
            sx={fieldLabelSx}
          >
            {strings.coachesLabel}
          </Typography>
          {isCoachesError ? (
            <Typography
              id="rosters-admin-create-cohort-coach-picker-error"
              variant="text-sm-regular"
              role="alert"
            >
              {noticeForError(refusalProse(coachesError), coachStrings.searchUnavailable)}
            </Typography>
          ) : null}
          <Autocomplete
            id="rosters-admin-create-cohort-coach-picker"
            sx={coachPickerSx}
            multiple
            options={institutionCoaches ?? []}
            value={coaches}
            onChange={(_event, next) => setCoaches(next)}
            inputValue={coachInput}
            onInputChange={(_event, next) => setCoachInput(next)}
            loading={isFetchingCoaches}
            filterOptions={(options) => options}
            getOptionLabel={(coach) => personName(coach)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={isCoachesError || !isRosterChosen}
            noOptionsText={
              debouncedCoachInput.trim().length > 0
                ? coachStrings.noSearchResults
                : coachStrings.noneAvailable
            }
            renderTags={(picked, getTagProps) =>
              picked.map((coach, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={coach.id}
                  id={`rosters-admin-create-cohort-coach-${coach.id}`}
                  size="small"
                  avatar={<Avatar src={coach.profile_image_url ?? undefined} />}
                  label={personName(coach)}
                />
              ))
            }
            renderOption={(props, coach) => (
              <Box component="li" {...props} key={coach.id} sx={optionRowSx}>
                <Avatar
                  src={coach.profile_image_url ?? undefined}
                  alt={personName(coach)}
                  sx={optionAvatarSx}
                >
                  {personInitial(coach)}
                </Avatar>
                <Typography variant="text-sm-medium">{personName(coach)}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={coaches.length === 0 ? strings.coachesPlaceholder : undefined}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <AppIcon
                          iconName="SearchMd"
                          diameter="20px"
                          color="Colors/Foreground/fg-quaternary"
                        />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {isFetchingCoaches ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        {isRostersUnavailable ? (
          <Typography
            id="rosters-admin-create-cohort-notice"
            variant="text-sm-regular"
            role="status"
          >
            {createStrings.rostersUnavailable}
          </Typography>
        ) : null}
        {isError ? (
          <Typography id="rosters-admin-create-cohort-error" variant="text-sm-regular" role="alert">
            {noticeForError(errorMessage, createStrings.error)}
          </Typography>
        ) : null}
      </Box>
    </WriteDialog>
  );
};

const formSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

/** The theme gives selects and autocompletes a darker resting border than text fields;
 * inside this form every control shares the text-field border. */
const rosterSelectSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  borderColor: backrs.colors['Colors/Border/border-primary'],
});

const coachPickerSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiAutocomplete-inputRoot': {
    borderColor: backrs.colors['Colors/Border/border-primary'],
  },
});

/** Matches LabeledFilterSelect's label-above-control spacing so every field reads the same. */
const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const fieldLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const codeRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
});

const optionRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const optionAvatarSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 32,
  height: 32,
  ...typography['text-sm-regular'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});
